import type { DOMAttributes, KeyboardEvent, SyntheticEvent } from "react";

export function stopPropagation(event: SyntheticEvent): void {
  event.stopPropagation();
}

/** Stops Escape, so underlying elements (e.g., the task detail) stay open. */
export function stopEscape(event: KeyboardEvent): void {
  if (event.key === "Escape") stopPropagation(event);
}

/** Keyboard activation for clickable elements that are not buttons. */
export function clickOnEnterOrSpace(event: KeyboardEvent<HTMLElement>): void {
  if (event.key !== "Enter" && event.key !== " ") return;

  event.preventDefault();
  event.currentTarget.click();
}

/** True while the user has text selected, e.g. right after dragging over it. */
export function hasTextSelection(): boolean {
  return window.getSelection()?.isCollapsed === false;
}

type BubblingHandlers<T extends Element> = Pick<
  DOMAttributes<T>,
  "onClick" | "onMouseDown" | "onPointerDown" | "onTouchStart"
>;

/**
 * Spread on portaled content (e.g., a popover) inside a clickable or draggable
 * parent. React events bubble through the component tree, not the DOM,
 * so e.g., a kanban card would open or start its drag.
 * Passed handlers still run, after the event is stopped.
 */
export function stopBubbling<T extends Element = HTMLElement>(
  handlers: BubblingHandlers<T> = {},
): Required<BubblingHandlers<T>> {
  return {
    onClick: stopThen(handlers.onClick),
    onMouseDown: stopThen(handlers.onMouseDown),
    onPointerDown: stopThen(handlers.onPointerDown),
    onTouchStart: stopThen(handlers.onTouchStart),
  };
}

function stopThen<E extends SyntheticEvent>(
  handler?: (event: E) => void,
): (event: E) => void {
  return (event) => {
    event.stopPropagation();
    handler?.(event);
  };
}
